import { Box, CircularProgress, Typography } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { fetchGamesPaginated } from "../services/api";
import GameGrid from "./GameGrid";
import GameList from "./GameList";
import SearchBar from "./SearchBar";

const Home = (props) => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(null);

  // URL search parameters
  const [queryParams, setQueryParams] = useSearchParams();

  // Derive search parameters from the URL
  const query = queryParams.get("query") || "";
  const genre = queryParams.get("genre") || "";
  const platform = queryParams.get("platform") || "";
  const scoreMin = Number(queryParams.get("scoreMin")) || 0;
  const scoreMax = Number(queryParams.get("scoreMax")) || 100;
  const orderBy = queryParams.get("orderBy") || "metacritic";
  const order = queryParams.get("order") || "desc";

  // Load initial page when search parameters change
  useEffect(() => {
    const loadInitial = async () => {
      setLoading(true);
      setPage(1);
      const score = `${scoreMin},${scoreMax}`;
      const { results, next, count } = await fetchGamesPaginated(query, genre, score, platform, 1, 40, orderBy, order);
      setGames(results);
      setHasMore(Boolean(next));
      setTotalCount(typeof count === 'number' ? count : null);
      setLoading(false);
    };
    loadInitial();
  }, [query, genre, platform, scoreMin, scoreMax, orderBy, order]);

  // Function to handle search and update query params
  const handleSearch = (query, genre, score, platform) => {
    const params = {};
    if (query) params.query = query;
    if (genre) params.genre = genre;
    if (platform) params.platform = platform;
    if (score[0] !== 0) params.scoreMin = score[0];
    if (score[1] !== 100) params.scoreMax = score[1];
    if (orderBy !== 'metacritic') params.orderBy = orderBy;
    if (order !== 'desc') params.order = order;
    setQueryParams(params);
  };

  // Function to handle sort change and update query params
  const handleSortChange = (newOrderBy, newOrder) => {
    setQueryParams({
      query,
      genre,
      platform,
      scoreMin,
      scoreMax,
      orderBy: newOrderBy,
      order: newOrder,
    });
  };

  // Infinite scroll: load more when reaching bottom
  const loadMoreGames = useCallback(async () => {
    setLoadingMore(true);
    const nextPage = page + 1;
    const score = `${scoreMin},${scoreMax}`;
    const { results, next, count } = await fetchGamesPaginated(query, genre, score, platform, nextPage, 40, orderBy, order);
    setGames(prev => {
      // Prevent duplicates by filtering out games with existing ids
      const existingIds = new Set(prev.map(game => game.id));
      const uniqueResults = results.filter(game => !existingIds.has(game.id));
      return [...prev, ...uniqueResults];
    });
    setPage(nextPage);
    setHasMore(Boolean(next));
    setTotalCount(typeof count === 'number' ? count : totalCount);
    setLoadingMore(false);
  }, [page, scoreMin, scoreMax, query, genre, platform, orderBy, order, totalCount]);

  useEffect(() => {
    const handleScroll = () => {
      if (loading || loadingMore || !hasMore) return;
      if (window.innerHeight + window.scrollY + 150 >= document.documentElement.scrollHeight) {
        loadMoreGames();
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [loading, loadingMore, hasMore, page, query, genre, platform, scoreMin, scoreMax, loadMoreGames]);

  return (
    <div className="home-container">
      <div className="sidebar">
        <SearchBar
          onSearch={handleSearch}
          darkMode={props.darkMode}
          initialQuery={query}
          initialGenre={genre}
          initialPlatform={platform}
          initialScore={[scoreMin, scoreMax]}
        />
      </div>
      <div>
        {typeof totalCount === 'number' && (
          <Box sx={{ mb: 2, fontWeight: 500, fontSize: 18 }}>
            {totalCount.toLocaleString()} result{totalCount === 1 ? '' : 's'} found
          </Box>
        )}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", height: "100vh" }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {props.view === "grid" ? (
              <GameList
                games={games}
                orderBy={orderBy}
                order={order}
                onSortChange={handleSortChange}
              />
            ) : (
              <GameGrid games={games} />
            )}
            {loadingMore && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <CircularProgress />
              </Box>
            )}
            {!hasMore && !loading && (
              <Box sx={{ textAlign: "center", mt: 2, mb: 2 }}>
                <Typography variant="body2">End of list</Typography>
              </Box>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Home;
